<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import FinestFieldGrid from '@/components/finest-field-grid/FinestFieldGrid.vue'
import type {
  InlineEditableCommitPayload,
  InlineEditableField,
  InlineEditableModel,
  InlineEditableSelectOption,
  InlineEditableValue,
} from '@/components/finest-field-grid/types'
import { useSyncedModel } from '@/composables/useSyncedModel'
import { usePersonUpdate } from '@/pages/investors/profile/usePersonUpdate'
import type { ExtendedEnum } from '@/types'
import type { FieldStateMap } from '@/utils/field-state'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import NaturalPersonData = App.Data.User.NaturalPersonData

const props = defineProps<{
  fieldStates: FieldStateMap
  person: NaturalPersonData
  salutationOptions: ExtendedEnum[]
  genderOptions: ExtendedEnum[]
  maritalStatusOptions: ExtendedEnum[]
  nationalityOptions: InlineEditableSelectOption[]
}>()

const { t } = useI18n()

const GERMANY = 'DE'

const editableKeys = [
  'salutation',
  'first_name',
  'last_name',
  'gender',
  'birth_date',
  'birth_place',
  'nationality',
  'in_germany_since',
  'tax_id',
  'marital_status',
  'address',
  'resident_since',
  'email',
  'phone',
]

const toOptions = (options: ExtendedEnum[], key: string) =>
  options.map((option) => ({ value: option.id, label: t(`${key}.${option.id}`) }))

const seedModel = (): InlineEditableModel => {
  const source = props.person as Record<string, InlineEditableValue>
  return Object.fromEntries(editableKeys.map((key) => [key, source[key] ?? null]))
}

const { model: personModel, confirm } = useSyncedModel(seedModel)

const update = usePersonUpdate(props.person.id ?? '', confirm)

const livesInGermanySince = computed(
  () => Boolean(personModel.value.nationality) && personModel.value.nationality !== GERMANY,
)

const personalFields = computed<InlineEditableField[]>(() => [
  {
    key: 'salutation',
    label: t('fields.salutation'),
    type: 'select',
    options: toOptions(props.salutationOptions, 'enums.salutation'),
  },
  {
    key: 'gender',
    label: t('fields.gender'),
    type: 'select',
    options: toOptions(props.genderOptions, 'gender'),
  },
  { key: 'first_name', label: t('fields.first_name'), type: 'text' },
  { key: 'last_name', label: t('fields.last_name'), type: 'text' },
  { key: 'birth_date', label: t('fields.birth_date'), type: 'date' },
  { key: 'birth_place', label: t('fields.birth_place'), type: 'text', fullWidth: true },
  {
    key: 'nationality',
    label: t('fields.nationality'),
    type: 'select',
    options: props.nationalityOptions,
  },
  ...(livesInGermanySince.value
    ? [{ key: 'in_germany_since', label: t('fields.in_germany_since'), type: 'date' as const }]
    : []),
  {
    key: 'marital_status',
    label: t('fields.marital_status'),
    type: 'select',
    options: toOptions(props.maritalStatusOptions, 'enums.maritalStatus'),
  },
  { key: 'tax_id', label: t('fields.tax_id'), type: 'text', fullWidth: true },
])

const contactFields = computed<InlineEditableField[]>(() => [
  { key: 'address', label: t('fields.address'), type: 'text', fullWidth: true },
  { key: 'email', label: t('fields.email'), type: 'text', fullWidth: true },
  { key: 'resident_since', label: t('fields.resident_since'), type: 'date' },
  { key: 'phone', label: t('fields.phone'), type: 'text' },
])

const save = ({ key, value }: InlineEditableCommitPayload) => update({ [key]: value })
</script>

<template>
  <div class="grid items-start gap-4 lg:grid-cols-2">
    <FinestCard :title="t('cards.personal')" icon="pi pi-user">
      <FinestFieldGrid v-model="personModel" :fields="personalFields" :field-states="fieldStates" @commit="save" />
    </FinestCard>

    <FinestCard :title="t('cards.contact')" icon="pi pi-envelope">
      <FinestFieldGrid v-model="personModel" :fields="contactFields" :field-states="fieldStates" @commit="save" />
    </FinestCard>
  </div>
</template>

<i18n>
{
  "de": {
    "cards": {
      "personal": "Persönliche Daten",
      "contact": "Kontaktdaten"
    },
    "fields": {
      "salutation": "Anrede",
      "first_name": "Vorname",
      "last_name": "Nachname",
      "gender": "Geschlecht",
      "birth_date": "Geburtsdatum",
      "birth_place": "Geburtsort",
      "nationality": "Staatsangehörigkeit",
      "in_germany_since": "In Deutschland seit",
      "tax_id": "Steuer-ID",
      "marital_status": "Familienstand",
      "address": "Adresse",
      "resident_since": "Wohnhaft seit",
      "email": "E-Mail",
      "phone": "Telefon"
    },
    "gender": {
      "male": "Männlich",
      "female": "Weiblich",
      "diverse": "Divers"
    }
  },
  "en": {
    "cards": {
      "personal": "Personal data",
      "contact": "Contact data"
    },
    "fields": {
      "salutation": "Salutation",
      "first_name": "First name",
      "last_name": "Last name",
      "gender": "Gender",
      "birth_date": "Date of birth",
      "birth_place": "Place of birth",
      "nationality": "Nationality",
      "in_germany_since": "In Germany since",
      "tax_id": "Tax ID",
      "marital_status": "Marital status",
      "address": "Address",
      "resident_since": "Resident since",
      "email": "Email",
      "phone": "Phone"
    },
    "gender": {
      "male": "Male",
      "female": "Female",
      "diverse": "Diverse"
    }
  }
}
</i18n>
